import { useEffect, useState } from 'react';
import { useForm } from '@mantine/form';
import {
  Alert,
  NumberInput,
  Stack,
  Text,
  Checkbox,
  Loader,
  Divider,
  Button,
  Group,
  Radio,
} from '@mantine/core';
import { AlertCircleIcon, LandmarkIcon, ReceiptIcon } from 'lucide-react';
import {
  initialState,
  OnSubmitPaymentProps,
  OnSubmitPaymentResult,
  paymentFormReducer,
  PaymentFormValues,
  SuggestedAmountFromBackend,
} from './paymentFormReducer';
import {
  PaymentMethodSelector,
  PaymentMethodType,
} from './PaymentMethodSelector';
import classes from './PaymentForm.module.css';
import { useFinixTokenForm } from './useFinixTokenForm';
import { useReducerWithSideEffects } from './reducerWithSideEffects';

interface Logger {
  info: (props: { message: string; extra?: Record<string, unknown> }) => void;
  warn: (props: { message: string; extra?: Record<string, unknown> }) => void;
  error: (props: {
    error: unknown;
    message?: string;
    extra?: Record<string, unknown>;
  }) => void;
}

export const PaymentForm = ({
  finixEnvironment,
  finixMerchantId,
  finixApplicationId,
  visible,
  firmId,
  matterId,
  firmName,
  initialTransactionAmount,
  paymentPageId,
  colorPalette,
  onSubmitSucceeded,
  availablePaymentMethods,
  onSubmitPayment,
  logger,
  accountType,
  suggestedAmount,
}: {
  finixEnvironment: 'sandbox' | 'live';
  finixMerchantId: string;
  finixApplicationId: string;
  visible: boolean;
  firmId: string;
  matterId: string;
  firmName: string;
  initialTransactionAmount: number | null;
  paymentPageId: string;
  colorPalette: {
    primary: string;
    secondary: string;
    tertiary?: string;
  };
  onSubmitSucceeded: (transactionData: {
    payFacTransactionId: string;
    amount: number;
    paymentMethod: PaymentMethodType;
    paymentInstrument: {
      id: string;
      accountType: string | undefined;
      bankCode: string | undefined;
      lastFour: string | undefined;
      brand: string | undefined;
      cardType: string | undefined;
      expirationMonth: number | undefined;
      expirationYear: number | undefined;
      name: string | undefined;
      maskedAccountNumber: string | undefined;
    };
  }) => void;
  availablePaymentMethods: {
    ach: boolean;
    creditCard: boolean;
  };
  onSubmitPayment: (
    props: OnSubmitPaymentProps,
  ) => Promise<OnSubmitPaymentResult>;
  logger?: Logger;
  accountType: 'trust' | 'operating';
  suggestedAmount: SuggestedAmountFromBackend | null;
}) => {
  const [state, dispatch] = useReducerWithSideEffects(
    paymentFormReducer,
    initialState,
    process.env.NODE_ENV === 'development', // Logging enabled in development
  );

  const suggestedAmountDollars =
    suggestedAmount !== null ? suggestedAmount.amountCents / 100 : 0;

  const form = useForm<PaymentFormValues>({
    initialValues: {
      firmId,
      matterId,
      paymentPageId,
      amount:
        suggestedAmount !== null
          ? suggestedAmountDollars
          : (initialTransactionAmount ?? 0),
      paymentMethod: 'card',
      savePaymentMethod: true,
      sendTransactionCompleteEmails: process.env.NODE_ENV !== 'development',
      finixEnvironment,
      finixMerchantId,
      finixApplicationId,
    },
    validate: {
      firmId: (value) => (!value ? 'Firm ID is required' : null),
      matterId: (value) => (!value ? 'Matter ID is required' : null),
      amount: (value) => (value <= 0 ? 'Amount must be greater than 0' : null),
    },
  });

  // 'suggested' = preselected radio, 'other' = client wants to type a custom amount.
  // When no suggestion exists, fall through to the bare NumberInput UI.
  const [amountSelection, setAmountSelection] = useState<'suggested' | 'other'>(
    'suggested',
  );
  const showOtherAmountInput =
    suggestedAmount === null || amountSelection === 'other';

  const handleAmountSelectionChange = (value: 'suggested' | 'other') => {
    setAmountSelection(value);
    if (value === 'suggested') {
      form.setFieldValue('amount', suggestedAmountDollars);
    } else {
      form.setFieldValue('amount', 0);
      logger?.info({
        message: 'suggested_amount_other_selected',
        extra: {
          paymentPageId,
          rule: suggestedAmount?.rule,
          suggestedAmountCents: suggestedAmount?.amountCents,
        },
      });
    }
  };

  useEffect(() => {
    if (suggestedAmount) {
      logger?.info({
        message: 'suggested_amount_shown',
        extra: {
          paymentPageId,
          rule: suggestedAmount.rule,
          amountCents: suggestedAmount.amountCents,
          accountType,
        },
      });
    }
    // Run once on mount; no-op when there's no suggestion to show.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedFinixFormState = useFinixTokenForm({
    environment: finixEnvironment,
    merchantId: finixMerchantId,
    containerId:
      form.values.paymentMethod === 'card'
        ? 'finix-form-card'
        : 'finix-form-bank',
    paymentMethod: form.values.paymentMethod,
  });

  const handleSubmit = (values: PaymentFormValues) => {
    logger?.info({
      message: 'Payment form submit button clicked',
      extra: {
        values,
      },
    });
    logger?.info({
      message: 'payment_form_submit',
      extra: {
        paymentPageId,
        selection: suggestedAmount === null ? 'noSuggestion' : amountSelection,
        amountCents: Math.round(values.amount * 100),
        suggestedAmountCents: suggestedAmount?.amountCents,
        suggestedAmountRule: suggestedAmount?.rule,
        accountType,
      },
    });
    dispatch({
      type: 'SUBMIT_CLICK',
      formValues: values,
      finixTokenFormState: selectedFinixFormState,
      onSubmitPayment: onSubmitPayment,
    });
  };

  useEffect(() => {
    if (state.status === 'SUBMIT_SUCCEEDED') {
      onSubmitSucceeded({
        payFacTransactionId: state.payFacTransactionId,
        amount: state.amount,
        paymentMethod: form.values.paymentMethod,
        paymentInstrument: state.paymentInstrument,
      });
    }
  }, [state, onSubmitSucceeded]);

  const disabled =
    state.status === 'SUBMITTING' || state.status === 'SUBMIT_SUCCEEDED';

  return (
    <div
      className={classes.paymentFormContainer}
      style={{
        display: visible ? 'flex' : 'none',
      }}
    >
      <AccountTypeBanner accountType={accountType} firmName={firmName} />
      <PaymentMethodSelector
        selectedPaymentMethod={form.values.paymentMethod}
        onPaymentMethodChange={(paymentMethod) => {
          form.setFieldValue('paymentMethod', paymentMethod);
        }}
        colorPalette={colorPalette}
        disableAch={!availablePaymentMethods.ach}
        disableCreditCard={!availablePaymentMethods.creditCard}
      />
      <form
        onSubmit={form.onSubmit((values) => {
          handleSubmit(values);
        })}
      >
        <Stack gap='xl'>
          <Stack gap='md'>
            <AmountSelector
              suggestedAmount={suggestedAmount}
              suggestedAmountDollars={suggestedAmountDollars}
              amountSelection={amountSelection}
              onAmountSelectionChange={handleAmountSelectionChange}
              showOtherAmountInput={showOtherAmountInput}
              form={form}
            />

            <Divider mt='md' />

            <div>
              {/* Keep both containers mounted; initialize only the selected method */}
              <div
                id='finix-form-card'
                style={{
                  display:
                    form.values.paymentMethod === 'card' &&
                    selectedFinixFormState.status === 'ready'
                      ? 'block'
                      : 'none',
                }}
              />
              <div
                id='finix-form-bank'
                style={{
                  display:
                    form.values.paymentMethod === 'bankAccount' &&
                    selectedFinixFormState.status === 'ready'
                      ? 'block'
                      : 'none',
                }}
              />
              {selectedFinixFormState.status !== 'ready' && (
                <Stack align='center' justify='center' h='500px'>
                  <Loader type='dots' color='pink.4' />
                </Stack>
              )}
            </div>

            {process.env.NODE_ENV === 'development' && (
              <Checkbox
                label='(Development only) Send transaction complete emails'
                key={form.key('sendTransactionCompleteEmails')}
                {...form.getInputProps('sendTransactionCompleteEmails', {
                  type: 'checkbox',
                })}
                disabled={disabled}
              />
            )}
          </Stack>

          {form.values.paymentMethod === 'card' && (
            <Alert
              color='gray'
              variant='light'
              styles={{
                root: { borderColor: 'var(--mantine-color-gray-3)' },
              }}
            >
              <Text size='sm' c='dark'>
                Payment processing is handled securely by Finix, a PCI-DSS Level
                1 certified provider. No payment information is stored or
                processed on our systems.
              </Text>
            </Alert>
          )}

          {form.values.paymentMethod === 'bankAccount' &&
            availablePaymentMethods.ach && (
              <Alert
                color='blue'
                variant='light'
                styles={{
                  root: { borderColor: 'var(--mantine-color-blue-3)' },
                }}
              >
                <Text size='sm' c='dark' fw={500}>
                  Authorization Agreement
                </Text>
                <Text size='sm' c='dark' mt='xs'>
                  By clicking submit, you authorize us to initiate an automated
                  clearing house (ACH) one-time debit in your name to your bank
                  account indicated above. The amount of this transaction as
                  noted above will be presented to your financial institution by
                  the next business day. You further agree that once you click
                  submit you may not revoke this authorization or cancel this
                  payment.
                </Text>
              </Alert>
            )}

          <Group justify='flex-end'>
            <Button
              type='submit'
              loading={state.status === 'SUBMITTING'}
              disabled={disabled || form.values.amount <= 0}
            >
              {form.values.amount > 0
                ? `Pay $${form.values.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : 'Pay'}
            </Button>
          </Group>
          {state.status === 'SUBMIT_FAILED' && (
            <Alert
              icon={<AlertCircleIcon size={16} />}
              title='Error'
              color='red'
            >
              {state.userMessage ??
                'An error occurred while processing your payment. Please try again.'}
            </Alert>
          )}
        </Stack>
      </form>
    </div>
  );
};

const AmountSelector = ({
  suggestedAmount,
  suggestedAmountDollars,
  amountSelection,
  onAmountSelectionChange,
  showOtherAmountInput,
  form,
}: {
  suggestedAmount: SuggestedAmountFromBackend | null;
  suggestedAmountDollars: number;
  amountSelection: 'suggested' | 'other';
  onAmountSelectionChange: (value: 'suggested' | 'other') => void;
  showOtherAmountInput: boolean;
  form: ReturnType<typeof useForm<PaymentFormValues>>;
}) => {
  if (suggestedAmount === null) {
    return <AmountInput form={form} autoFocus={false} />;
  }

  const formattedAmount = suggestedAmountDollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <Radio.Group
      label='Amount'
      value={amountSelection}
      onChange={(value) =>
        onAmountSelectionChange(value as 'suggested' | 'other')
      }
    >
      <Stack gap='xs' mt='xs'>
        <Radio.Card
          radius='md'
          value='suggested'
          p='md'
          style={{
            borderColor:
              amountSelection === 'suggested'
                ? 'var(--mantine-color-blue-5)'
                : undefined,
            borderWidth: amountSelection === 'suggested' ? 2 : 1,
          }}
        >
          <Group wrap='nowrap' align='flex-start' gap='sm'>
            <Radio.Indicator />
            <Stack gap={2} style={{ flex: 1 }}>
              <Text size='sm' fw={600}>
                {suggestedAmount.label}
              </Text>
              <Text size='xl' fw={700}>
                ${formattedAmount}
              </Text>
              <Text size='xs' c='dimmed' fs='italic'>
                {suggestedAmount.description}
              </Text>
            </Stack>
          </Group>
        </Radio.Card>
        <Radio.Card
          radius='md'
          value='other'
          p='md'
          style={{
            borderColor:
              amountSelection === 'other'
                ? 'var(--mantine-color-blue-5)'
                : undefined,
            borderWidth: amountSelection === 'other' ? 2 : 1,
          }}
        >
          <Group wrap='nowrap' align='center' gap='sm'>
            <Radio.Indicator />
            <Text size='sm'>Other amount</Text>
          </Group>
          {showOtherAmountInput && amountSelection === 'other' && (
            <div style={{ marginTop: 12 }}>
              <AmountInput form={form} autoFocus />
            </div>
          )}
        </Radio.Card>
      </Stack>
    </Radio.Group>
  );
};

const AmountInput = ({
  form,
  autoFocus,
}: {
  form: ReturnType<typeof useForm<PaymentFormValues>>;
  autoFocus: boolean;
}) => (
  <NumberInput
    required
    label={autoFocus ? undefined : 'Amount'}
    placeholder='0.00'
    key={form.key('amount')}
    {...form.getInputProps('amount')}
    prefix='$'
    decimalScale={2}
    min={0.0}
    autoFocus={autoFocus}
    // Force the iOS decimal keypad. Without this, react-number-format leaves
    // `inputMode` unset on iPhone (intentional carve-out), and iOS heuristically
    // picks a numeric keypad with no `.` key.
    inputMode='decimal'
    // Setting `step={0}` to disable the ability to increment/decrement value
    // using arrow keys. We've been seeing issues where the originally-entered
    // value changes by one or two cents by the time the form is submitted. The
    // theory is that the user is unintentionally adjusting the value before
    // moving to the next field.
    step={0}
    // These are the increment/decrement buttons on the left-hand side of the
    // input field. We've set the `step` to 0, so hiding these buttons because
    // they're useless.
    hideControls
    styles={{
      control: {
        boxShadow: 'inherit',
        padding: 'inherit',
        margin: 'inherit',
        border: 'inherit',
        color: 'inherit',
      },
    }}
  />
);

const AccountTypeBanner = ({
  accountType,
  firmName,
}: {
  accountType: 'trust' | 'operating';
  firmName: string;
}) => {
  if (accountType === 'trust') {
    return (
      <Alert
        color='violet'
        variant='light'
        icon={<LandmarkIcon size={20} />}
        title='Retainer deposit'
        styles={{
          root: { borderColor: 'var(--mantine-color-violet-3)' },
          title: { fontWeight: 700 },
        }}
      >
        <Text size='sm'>Held in the firm's trust account until earned.</Text>
      </Alert>
    );
  }
  return (
    <Alert
      color='blue'
      variant='light'
      icon={<ReceiptIcon size={20} />}
      title='Invoice payment'
      styles={{
        root: { borderColor: 'var(--mantine-color-blue-3)' },
        title: { fontWeight: 700 },
      }}
    >
      <Text size='sm'>Paid to {firmName} for services rendered.</Text>
    </Alert>
  );
};
